import type { EnMappingDecisions, EnMappingModel, EnNiveauOption } from './types';

/**
 * Décisions par défaut des correspondances Éducation nationale : les niveaux
 * sont pré-remplis depuis l'auto-match (liste complète, normalisation de
 * libellé) avec leur `codeCycle` dérivé ; les MEF restent à choisir (recherche),
 * et les matières sont initialisées vides par (niveau, nom) — leur auto-match
 * dépend du MEF choisi et se fait quand les options du niveau sont chargées.
 */
export function initEnDecisions(model: EnMappingModel): EnMappingDecisions {
  const cycleByNiveauCode = new Map<string, string | null>(
    model.options.niveaux.map((n) => [n.code, n.codeCycle]),
  );

  const niveaux: EnMappingDecisions['niveaux'] = {};
  for (const v of model.niveaux.values) {
    niveaux[v.value] = {
      niveauCode: v.niveauCode,
      codeCycle: v.niveauCode ? cycleByNiveauCode.get(v.niveauCode) ?? null : null,
      mefCode: null,
      mefLabel: null,
    };
  }

  const matieres: EnMappingDecisions['matieres'] = {};
  for (const v of model.matieres.values) {
    if (!matieres[v.niveau]) matieres[v.niveau] = {};
    matieres[v.niveau][v.nom] = { matiereCode: null, matiereLabel: null };
  }

  return { niveaux, matieres };
}

/** Code cycle dérivé d'un code niveau EN (lecture seule dans l'UI). */
export function cycleForNiveau(
  options: EnNiveauOption[],
  niveauCode: string | null,
): string | null {
  if (!niveauCode) return null;
  return options.find((o) => o.code === niveauCode)?.codeCycle ?? null;
}

/**
 * Normalise un libellé pour l'auto-match (insensible casse/accents/ponctuation).
 * Reflète `CsvUtil::normalizeLabelKey` côté backend.
 */
export function normalizeLabel(label: string): string {
  return label
    .normalize('NFD')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .toUpperCase();
}
